
import { ProtoUtil } from "src/app/util/proto-util";
import * as protos from "../../../../assets/proto/compiled/all";
import { LocalizationService } from "../../../core/services/localization.service";
import { Formatter } from "../../../util/formatter";
import { Util } from "../../../util/util";
import { CategoryWithMenuItemsGroup, ComboItemExtendedLinkProducts, EditorCategory, EditorItem, EditorItemSelection, EditorSchedule, LinkProductsDialog, MenuItemExtendedLinkProducts } from "./interfaces";
import { menu } from "ionicons/icons";

export class MenuEditorUtil {
  static getMenuEntryId(
    menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto,
  ) {
    if (menuEntry?.menuItem) {
      return Util.safeString(menuEntry?.menuItem.id);
    } else if (menuEntry?.combo) {
      return Util.safeString(menuEntry?.combo.id);
    }
    return '';
  }

  static itemSpecToEditorItem(itemSpec: protos.waiternow.common.MenuSpecProto.IItemProto): EditorItem {
    const currencyCode: string = itemSpec?.price?.currencyCode ?? '';
    return {
      id: Util.safeString(itemSpec.id),
      nameEn: Formatter.getNullableText(itemSpec.name, protos.waiternow.common.Language.ENGLISH),
      nameEs: Formatter.getNullableText(itemSpec.name, protos.waiternow.common.Language.SPANISH),
      price: itemSpec.price ? ProtoUtil.moneyProtoToPickerMoney(itemSpec.price, currencyCode) : undefined,
    };
  }

  static findItemSpec(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): protos.waiternow.common.MenuSpecProto.IMenuItemProto | undefined {
    for (const itemSpec of menuSpec.items || []) {
      if (itemSpec.id == id) {
        return itemSpec;
      }
    }
    return undefined;
  }

  static getItemSelectionSpecItemsAsEditorItems(
    itemSelectionSpec: protos.waiternow.common.MenuSpecProto.IItemSelectionProto,
    menuSpec: protos.waiternow.common.IMenuSpecProto): Array<EditorItem> {
    const editorItems: Array<EditorItem> = [];
    for (const itemId of itemSelectionSpec.itemIds || []) {
      const itemSpec = MenuEditorUtil.findItemSpec(itemId, menuSpec);
      if (itemSpec) {
        editorItems.push(this.itemSpecToEditorItem(itemSpec));
      }
    }
    return editorItems;
  }

  static getItemSelectionSpecItemsAsItems(
    itemSelectionSpec: protos.waiternow.common.MenuSpecProto.IItemSelectionProto,
    menuSpec: protos.waiternow.common.IMenuSpecProto): Array<protos.waiternow.common.MenuSpecProto.IMenuItemProto> {
    const editorItems: Array<protos.waiternow.common.MenuSpecProto.IMenuItemProto> = [];
    for (const itemId of itemSelectionSpec.itemIds || []) {
      const itemSpec = MenuEditorUtil.findItemSpec(itemId, menuSpec);
      if (itemSpec) {
        editorItems.push(itemSpec);
      }
    }
    return editorItems;
  }

  static itemSelectionSpecToEditorItemSelection(
    itemSelectionSpec: protos.waiternow.common.MenuSpecProto.IItemSelectionProto,
    menuSpec: protos.waiternow.common.IMenuSpecProto): EditorItemSelection {
    return {
      id: Util.safeString(itemSelectionSpec.id),
      titleEn: Formatter.getNullableText(itemSelectionSpec.title, protos.waiternow.common.Language.ENGLISH),
      titleEs: Formatter.getNullableText(itemSelectionSpec.title, protos.waiternow.common.Language.SPANISH),
      isRequired: itemSelectionSpec.isRequired ? true : false,
      isMultipleSelection: itemSelectionSpec.selectionType == protos.waiternow.common.SelectionType.MULTIPLE ? true : false,
      maxSelections: itemSelectionSpec.maxSelections ? itemSelectionSpec.maxSelections : undefined,
      items: MenuEditorUtil.getItemSelectionSpecItemsAsEditorItems(itemSelectionSpec, menuSpec),
    };
  }

  static getAllItemSelectionSpecsAsEditorItemSelections(
    menuSpec: protos.waiternow.common.IMenuSpecProto,
    itemSelectionIdsToExclude: Array<string>): Array<EditorItemSelection> {
    const itemSelections: Array<EditorItemSelection> = [];
    for (const itemSelectionSpec of menuSpec.itemSelections || []) {
      if (!Util.arrayContains(itemSelectionSpec.id, itemSelectionIdsToExclude)) {
        itemSelections.push(MenuEditorUtil.itemSelectionSpecToEditorItemSelection(itemSelectionSpec, menuSpec));
      }
    }
    return itemSelections;
  }

  static getMenuEntryComboSpecId(
    menuEntrySpec: protos.waiternow.common.MenuSpecProto.IComboProto
  ) {
    return Util.safeString(menuEntrySpec.id);
  }

  static getMenuEntrySpecId(
    menuEntrySpec: protos.waiternow.common.MenuSpecProto.IMenuItemProto,
  ) {
    return Util.safeString(menuEntrySpec.id);
  }

  static getCategoryId(
    category: protos.waiternow.common.MenuProto.ICategoryLevel1Proto,
  ) {
    return Util.safeString(category.id);
  }

  static getCategoryName(
    category: protos.waiternow.common.MenuProto.ICategoryLevel1Proto,
    localizationService: LocalizationService,
  ) {
    return this.getNonEmptyText(category.name, localizationService);
  }

  static getNonEmptyText(
    textProto: protos.waiternow.common.ITextProto | null | undefined,
    localizationService: LocalizationService,
  ): string {
    let str: string | null | undefined;
    if (localizationService.isSpanish()) {
      str = Formatter.getText(
        textProto,
        protos.waiternow.common.Language.SPANISH,
      );
    } else {
      str = Formatter.getText(
        textProto,
        protos.waiternow.common.Language.ENGLISH,
      );
    }
    if (!str) {
      str = textProto?.text;
    }
    return Util.safeString(str);
  }

  static findCategorySpecIndex(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): number {
    let index = 0;
    for (const categorySpec of menuSpec.categories || []) {
      if (categorySpec.id == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static findMenuItemSpecIndex(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): number {
    let index = 0;
    for (const menuItemSpec of menuSpec.menuItems || []) {
      if (menuItemSpec.id == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static findComboItemSpecIndex(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): number {
    let index = 0;
    for (const comboItemSpec of menuSpec.combos || []) {
      if (comboItemSpec.id == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static findCategoryIndex(
    id: string | null | undefined,
    menu: protos.waiternow.common.IMenuProto,
  ): number {
    let index = 0;
    for (const category of menu.categories || []) {
      if (category.id == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static findMenuEntryIndex(
    id: string | null | undefined,
    category: protos.waiternow.common.MenuProto.ICategoryLevel1Proto,
  ): number {
    let index = 0;
    for (const menuEntry of category.menuEntries || []) {
      if (MenuEditorUtil.getMenuEntryId(menuEntry) == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static findItemSelectionSpec(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): protos.waiternow.common.MenuSpecProto.IItemSelectionProto | undefined {
    for (const itemSelectionSpec of menuSpec.itemSelections || []) {
      if (itemSelectionSpec.id == id) {
        return itemSelectionSpec;
      }
    }
    return undefined;
  }

  static findItemSelectionSpecIndexInMenuItemSpec(
    id: string | null | undefined,
    menuItemSpec: protos.waiternow.common.MenuSpecProto.IMenuItemProto,
  ): number {
    let index = 0;
    for (const itemSelectionSpecId of menuItemSpec.itemSelectionIds || []) {
      if (itemSelectionSpecId == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static findItemSelectionIndex(
    id: string | null | undefined,
    menuItem: protos.waiternow.common.MenuProto.IMenuItemProto,
  ): number {
    let index = 0;
    for (const itemSelection of menuItem.itemSelections || []) {
      if (itemSelection.id == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static findMenuItemSpec(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): protos.waiternow.common.MenuSpecProto.IMenuItemProto | undefined {
    for (const menuItemSpec of menuSpec.menuItems || []) {
      if (menuItemSpec.id == id) {
        return menuItemSpec;
      }
    }
    return undefined;
  }

  static async convertImageToBuffer(file: File) {
    // Await the ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    // Create the Uint8Array
    const uint8Array = new Uint8Array(arrayBuffer);

    return uint8Array;
  }

  static availabilityScheduleProtoToEditorSchedule(
    availabilityScheduleProto: protos.waiternow.common.IAvailabilityScheduleProto,
  ): EditorSchedule {
    let dailyRecurrenceProto:
      | protos.waiternow.common.IDailyRecurrenceProto
      | undefined = undefined;
    if (
      availabilityScheduleProto.schedule &&
      availabilityScheduleProto.schedule.dailyRecurrences &&
      availabilityScheduleProto.schedule.dailyRecurrences?.length > 0
    ) {
      dailyRecurrenceProto =
        availabilityScheduleProto.schedule?.dailyRecurrences[0];
    }
    const editorSchedule: EditorSchedule = {
      id: Util.safeString(availabilityScheduleProto.id),
      dailyRecurrence: {
        recurrenceType:
          dailyRecurrenceProto && dailyRecurrenceProto.recurrenceType
            ? dailyRecurrenceProto.recurrenceType
            : protos.waiternow.common.DailyRecurrenceProto.RecurrenceType.DAILY,
      },
    };
    if (dailyRecurrenceProto?.timeRange) {
      if (dailyRecurrenceProto?.timeRange.from) {
        editorSchedule.dailyRecurrence.startsAtHour =
          dailyRecurrenceProto?.timeRange.from.hour || 0;
        editorSchedule.dailyRecurrence.startsAtMinute =
          dailyRecurrenceProto?.timeRange.from.minute || 0;
      }
      if (dailyRecurrenceProto?.timeRange.to) {
        editorSchedule.dailyRecurrence.endsAtHour =
          dailyRecurrenceProto?.timeRange.to.hour || 0;
        editorSchedule.dailyRecurrence.endsAtMinute =
          dailyRecurrenceProto?.timeRange.to.minute || 0;
      }
      if (availabilityScheduleProto.schedule?.isInverted) {
        editorSchedule.isInverted = true;
      }
    }
    return editorSchedule;
  }

  static editorScheduleToAvailabilityScheduleProto(
    editorSchedule: EditorSchedule,
  ): protos.waiternow.common.IAvailabilityScheduleProto {
    const availabilityShceduleProto =
      new protos.waiternow.common.AvailabilityScheduleProto();
    availabilityShceduleProto.id = Util.safeString(editorSchedule.id);
    availabilityShceduleProto.schedule =
      new protos.waiternow.common.ScheduleProto();
    availabilityShceduleProto.schedule.dailyRecurrences = [];
    if (
      editorSchedule.dailyRecurrence &&
      editorSchedule.dailyRecurrence.recurrenceType
    ) {
      const dailyRecurrenceProto =
        new protos.waiternow.common.DailyRecurrenceProto();
      dailyRecurrenceProto.recurrenceType =
        editorSchedule.dailyRecurrence.recurrenceType;
      if (
        editorSchedule.dailyRecurrence.startsAtHour ||
        editorSchedule.dailyRecurrence.endsAtHour
      ) {
        dailyRecurrenceProto.timeRange =
          new protos.waiternow.common.TimeRangeProto();
        if (editorSchedule.dailyRecurrence.startsAtHour) {
          dailyRecurrenceProto.timeRange.from =
            new protos.waiternow.common.TimeProto();
          dailyRecurrenceProto.timeRange.from.hour =
            editorSchedule.dailyRecurrence.startsAtHour;
          dailyRecurrenceProto.timeRange.from.minute =
            editorSchedule.dailyRecurrence.startsAtMinute || 0;
        }
        if (editorSchedule.dailyRecurrence.endsAtHour) {
          dailyRecurrenceProto.timeRange.to =
            new protos.waiternow.common.TimeProto();
          dailyRecurrenceProto.timeRange.to.hour =
            editorSchedule.dailyRecurrence.endsAtHour;
          dailyRecurrenceProto.timeRange.to.minute =
            editorSchedule.dailyRecurrence.endsAtMinute || 0;
        }
        if (editorSchedule.isInverted) {
          availabilityShceduleProto.schedule.isInverted = true;
        }
      }
      availabilityShceduleProto.schedule.dailyRecurrences.push(
        dailyRecurrenceProto,
      );
    }
    return availabilityShceduleProto;
  }

  static getAllSchedulesAsEditorSchedules(
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): Array<EditorSchedule> {
    const schedules: Array<EditorSchedule> = [];
    for (const availabilityScheduleProto of menuSpec.schedules || []) {
      schedules.push(
        MenuEditorUtil.availabilityScheduleProtoToEditorSchedule(
          availabilityScheduleProto,
        ),
      );
    }
    return schedules;
  }

  static findAvailabilityScheduleSpecIndex(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): number {
    let index = 0;
    for (const availabilitySchedule of menuSpec.schedules || []) {
      if (availabilitySchedule.id == id) {
        return index;
      }
      index++;
    }
    return -1;
  }

  static categoryToEditorCategory(
    category: protos.waiternow.common.MenuProto.ICategoryLevel1Proto,
  ): EditorCategory {
    return {
      id: Util.safeString(category.id),
      nameEn: Formatter.getNullableText(
        category.name,
        protos.waiternow.common.Language.ENGLISH,
      ),
      nameEs: Formatter.getNullableText(
        category.name,
        protos.waiternow.common.Language.SPANISH,
      ),
    };
  }

  static categorySpecToEditorCategory(
    categorySpec: protos.waiternow.common.MenuSpecProto.ICategoryProto,
  ): EditorCategory {
    return {
      id: Util.safeString(categorySpec.id),
      nameEn: Formatter.getNullableText(
        categorySpec.name,
        protos.waiternow.common.Language.ENGLISH,
      ),
      nameEs: Formatter.getNullableText(
        categorySpec.name,
        protos.waiternow.common.Language.SPANISH,
      ),
    };
  }

  static getAllCategorySpecsAsEditorCategories(
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): Array<EditorCategory> {
    const categories: Array<EditorCategory> = [];
    for (const categorySpec of menuSpec.categories || []) {
      categories.push(
        MenuEditorUtil.categorySpecToEditorCategory(categorySpec),
      );
    }
    return categories;
  }

  static getMenuEntryCategoryIds(
    menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): Array<string> {
    const menuEntryId = MenuEditorUtil.getMenuEntryId(menuEntry);
    if (menuEntry.menuItem) {
      const menuItemSpec = MenuEditorUtil.findMenuItemSpec(
        menuEntryId,
        menuSpec,
      );
      if (!menuItemSpec) {
        return [];
      }
      return menuItemSpec.categoryIds || [];
    } else {
      const comboSpec = MenuEditorUtil.findComboSpec(menuEntryId, menuSpec);
      if (!comboSpec) {
        return [];
      }
      return comboSpec.categoryIds || [];
    }
  }

  static findComboSpec(
    id: string | null | undefined,
    menuSpec: protos.waiternow.common.IMenuSpecProto,
  ): protos.waiternow.common.MenuSpecProto.IComboProto | undefined {
    for (const comboSpec of menuSpec.combos || []) {
      if (comboSpec.id == id) {
        return comboSpec;
      }
    }
    return undefined;
  }

  static editedItemSelectionToItemSelectionSpec(editedItemSelection: protos.waiternow.common.MenuSpecProto.IMenuItemSelectionProto): protos.waiternow.common.MenuSpecProto.IItemSelectionProto {
    const itemSelectonSpec = new protos.waiternow.common.MenuSpecProto.ItemSelectionProto();
    itemSelectonSpec.id = Util.safeString(editedItemSelection.id);
    itemSelectonSpec.title = editedItemSelection.title;
    itemSelectonSpec.isRequired = editedItemSelection.isRequired ? editedItemSelection.isRequired : false;
    itemSelectonSpec.selectionType = editedItemSelection.selectionType ? editedItemSelection.selectionType : protos.waiternow.common.SelectionType.SINGLE;
    itemSelectonSpec.maxSelections = editedItemSelection.maxSelections ? editedItemSelection.maxSelections : 0;
    // Items ids are not copied because they may be new items wothout id. This is done by the menu editor.
    itemSelectonSpec.itemIds = [];
    return itemSelectonSpec;
  }

  static getMenuEntryRemovableIngredients(menuEntry: protos.waiternow.common.MenuProto.IMenuEntryProto): Array<protos.waiternow.common.MenuProto.IItemProto> {
    if (menuEntry?.menuItem?.removableIngredients) {
      return menuEntry?.menuItem?.removableIngredients;
    }
    return [];
  }

  static editorItemToItem(dialogItem: EditorItem): protos.waiternow.common.MenuProto.IItemProto {
    const item = new protos.waiternow.common.MenuProto.ItemProto();
    item.id = Util.safeString(dialogItem.id);
    item.name = ProtoUtil.createTextProto(dialogItem.nameEn, dialogItem.nameEs);
    item.price = dialogItem.price ? ProtoUtil.pickerMoneyToMoneyProto(dialogItem.price) : undefined;
    return item;
  }

  static editorItemToItemSpec(dialogItem: EditorItem): protos.waiternow.common.MenuSpecProto.IItemProto {
    const item = new protos.waiternow.common.MenuSpecProto.ItemProto();
    item.id = Util.safeString(dialogItem.id);
    item.name = ProtoUtil.createTextProto(dialogItem.nameEn, dialogItem.nameEs);
    item.price = dialogItem.price ? ProtoUtil.pickerMoneyToMoneyProto(dialogItem.price) : undefined;
    return item;
  }

  static getItemSelectionItemsAsEditorItems(
      itemSelection: protos.waiternow.common.MenuProto.IItemSelectionProto, currencyCode: string): Array<EditorItem> {
    const editorItems: Array<EditorItem> = [];
    for (const item of itemSelection.items || []) {
      editorItems.push(this.itemToEditorItem(item, currencyCode));
    }
    return editorItems;
  }

  static itemToEditorItem(item: protos.waiternow.common.MenuProto.IItemProto, currencyCode: string): EditorItem {
    return {
      id: Util.safeString(item.id),
      nameEn: Formatter.getNullableText(item.name, protos.waiternow.common.Language.ENGLISH),
      nameEs: Formatter.getNullableText(item.name, protos.waiternow.common.Language.SPANISH),
      price: item.price ? ProtoUtil.moneyProtoToPickerMoney(item.price, currencyCode) : undefined,
    }
  }

  static editorItemSelectionToItemSelection(editorItemSelection: EditorItemSelection): protos.waiternow.common.MenuProto.IItemSelectionProto {
    const itemSelecton = new protos.waiternow.common.MenuProto.ItemSelectionProto();
    itemSelecton.id = Util.safeString(editorItemSelection.id);
    itemSelecton.title = ProtoUtil.createTextProto(editorItemSelection.titleEn, editorItemSelection.titleEs);
    itemSelecton.isRequired = editorItemSelection.isRequired ? editorItemSelection.isRequired : false;
    itemSelecton.selectionType = editorItemSelection.isMultipleSelection ? protos.waiternow.common.SelectionType.MULTIPLE : protos.waiternow.common.SelectionType.SINGLE;
    itemSelecton.maxSelections = editorItemSelection.isMultipleSelection && editorItemSelection.maxSelections ? editorItemSelection.maxSelections : 0;
    // Items ids are not copied because they may be new items wothout id. This is done by the menu editor.
    itemSelecton.items = [];
    return itemSelecton;
  }

  static findCategorySpec(
      id: string | null | undefined,
      menuSpec: protos.waiternow.common.IMenuSpecProto,
    ) : protos.waiternow.common.MenuSpecProto.IMenuItemProto | undefined {
    for (const categorySpec of menuSpec.categories || []) {
      if (categorySpec.id == id) {
        return categorySpec;
      }
    }
    return undefined;
  }

  static findMenuEntryWithCategory(
    id: string | null | undefined,
    categoryId: string | null | undefined,
    menu: protos.waiternow.common.IMenuProto
  ): protos.waiternow.common.MenuProto.IMenuEntryProto | undefined {
    if (!id || !categoryId) return undefined;

    const categoryProto = menu.categories?.find(cat => Util.safeString(cat.id) === categoryId);
    return categoryProto?.menuEntries?.find(
      entry => this.getMenuEntryId(entry) === id
    );
  }

  static findAllMenuItemOccurrences(
    id: string | null | undefined /* MenuEntry ID */,
    menu: protos.waiternow.common.IMenuProto
  ) {
    if (!id || !menu.categories) return undefined;
    // console.log(menu);

    const foundItems: protos.waiternow.common.MenuProto.IMenuItemProto[] = [];

    for (const cat of menu.categories) {
      for (const entry of cat.menuEntries || []) {

        if (entry.menuItem && Util.safeString(entry.menuItem.id) === id) {
          foundItems.push(entry.menuItem);
        }

        if (entry.combo?.menuItems) {
          for (const comboItem of entry.combo.menuItems) {
            if (Util.safeString(comboItem.id) === id) {
              foundItems.push(comboItem);
            }
          }
        }

      }
    }
    return foundItems;
  }

  static findMenuItemSpecFromItemSelection(
    menuSpec: protos.waiternow.common.MenuSpecProto,
    itemSelectionId: string | null | undefined
  ): Map<string, CategoryWithMenuItemsGroup> {

    const categoryMap = new Map<string, CategoryWithMenuItemsGroup>();
    const itemSelectionSpec = this.findItemSelectionSpec(itemSelectionId, menuSpec);
    if (!itemSelectionSpec) {
      return categoryMap;
    }

    for (const menuItemSpec of menuSpec.menuItems || []) {
      if (!menuItemSpec.id) continue;

      const isCorrelated = Util.arrayContains(itemSelectionId, menuItemSpec.itemSelectionIds);

      const extendedMenuItem: MenuItemExtendedLinkProducts = {
        ...menuItemSpec,
        hasSelection: isCorrelated,
      };

      for (const categoryId of menuItemSpec.categoryIds ?? []) {
        const category = this.findCategorySpec(categoryId, menuSpec);

        if (category) {
          if (!categoryMap.has(categoryId)) {
            categoryMap.set(categoryId, {
              category: { ...category, hasSelection: false, expanded: false },
              menuItems: [],
              combos: []
            });
          }
          categoryMap.get(categoryId)!.menuItems.push(extendedMenuItem);
        }
      }
    }

    for (const comboSpec of menuSpec.combos || []) {
      if (!comboSpec.id) continue;

      const comboMenuItemIds: string[] = comboSpec.menuItemIds ?? [];
      const availableMenuItems: protos.waiternow.common.MenuSpecProto.IMenuItemProto[] = [];
      const selectedChildItemsIds: string[] = [];

      for (const menuItemId of comboMenuItemIds) {
        const menuItemSpec = this.findMenuItemSpec(menuItemId, menuSpec);

        if (menuItemSpec) {
          availableMenuItems.push(menuItemSpec);

          const itemHasSelection = Util.arrayContains(itemSelectionId, menuItemSpec.itemSelectionIds);
          if (itemHasSelection && menuItemSpec.id) {
            selectedChildItemsIds.push(menuItemSpec.id);
          }
        }
      }

      const extendedCombo: ComboItemExtendedLinkProducts = {
        ...comboSpec,
        hasSelection: selectedChildItemsIds.length > 0,
        availableMenuItems: availableMenuItems,
        selectedChildItemIds: selectedChildItemsIds
      };

      for (const categoryId of comboSpec.categoryIds || []) {
        const category = this.findCategorySpec(categoryId, menuSpec);
        if (category) {
          if (!categoryMap.has(categoryId)) {
            categoryMap.set(categoryId, {
              category: { ...category, hasSelection: false, expanded: false },
              menuItems: [],
              combos: []
            });
          }
          categoryMap.get(categoryId)!.combos.push(extendedCombo);
        }
      }
    }

    for (const group of categoryMap.values()) {
      const allElements = [...group.menuItems, ...group.combos];
      if (allElements.length > 0) {
        group.category.hasSelection = allElements.every(item => item.hasSelection);
      }
    }

    return categoryMap;
  }

  static getMenuItemSpecFromItemSelection(
    menuSpec: protos.waiternow.common.MenuSpecProto,
    itemSelectionId: string
  ): LinkProductsDialog {
    let categoryMap = new Map<string, CategoryWithMenuItemsGroup>();
    categoryMap = this.findMenuItemSpecFromItemSelection(menuSpec, itemSelectionId);
    return {
      categoriesWithItems: Array.from(categoryMap.values())
    };
  }

  static createComboProtoSpec() {
    const proto = new protos.waiternow.common.MenuSpecProto.ComboProto;
    proto.categoryIds = ['category-id-1'];
    proto.id = 'id-combo-one-egg-any-style';
    proto.itemSelectionIds = [];
    proto.menuItemIds = ['menu-item-id-1','menu-item-id-76'];
    proto.menuItemSelectionIds = ['id-menu-item-selection-one-egg-any-style-sides'];
    proto.name = ProtoUtil.createTextProto('One Egg any style Combo', 'Un Huevo a cualquier estilo combo');
    proto.price = ProtoUtil.createMoneyProto(9, 50, 'USD');
    return proto;
  }

  static createMenuItemSelectionProtoSpec() {
    const menuItemSelectionProto = new protos.waiternow.common.MenuSpecProto.MenuItemSelectionProto;
    menuItemSelectionProto.id = 'id-menu-item-selection-one-egg-any-style-sides';
    menuItemSelectionProto.isRequired = true;
    menuItemSelectionProto.menuItemIds = ['menu-item-id-40', 'menu-item-id-41', 'menu-item-id-42'];
    menuItemSelectionProto.selectionType = 1;
    menuItemSelectionProto.title = ProtoUtil.createTextProto('Sides', 'Acompañantes');
    return menuItemSelectionProto
  }

  static isMenuItemInComboSpec(
    idMenuItem: string | null | undefined,
    menuSpec: protos.waiternow.common.MenuSpecProto
  ) {
    if (!idMenuItem || !menuSpec) return false;

    for (const combo of menuSpec.combos || []) {
      const menuItemIds = combo.menuItemIds ?? [];
      const comboHasMenuItem: boolean = Util.arrayContains(idMenuItem, menuItemIds);
      if (comboHasMenuItem) return true;
    }
    return false;
  }

  

}
